import { useState, useCallback } from 'react';
import { AssetsFilter } from '../models';
import { GlobalButton, SelectInput, TextInput } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  Search as SearchIcon,
  DeleteForever as TrashIcon,
} from '@mui/icons-material';
import { useAssetOptions, useOfficesOptions } from '../hooks';
import AutocompleteInput, {
  Option,
} from '@/components/inputs/AutocompleteInput';
import { useUsersOptions } from '../../../hooks';
import { BrandAutocompleteInput } from '../Brands/components';
import { TypeAutocompleteInput } from '../Types/components';
import { AssetModelAutocompleteInput } from '../AssetModels/components';

interface SearchBarProps {
  initialFilter: AssetsFilter;
  filter: AssetsFilter;
  search: (updatedFilter: AssetsFilter) => void;
}

const SearchBar = ({ initialFilter, filter, search }: SearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<AssetsFilter>(filter);
  const [userInput, setUserInput] = useState('');
  const [selectedUser, setSelectedUser] = useState<Option | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<Option | null>(null);
  const [selectedType, setSelectedType] = useState<Option | null>(null);
  const [selectedModel, setSelectedModel] = useState<Option | null>(null);
  const [selectedOffice, setSelectedOffice] = useState<Option | null>(null);
  const [officeInput, setOfficeInput] = useState('');

  const resetFilter = () => {
    setLocalFilter(initialFilter);
    setUserInput('');
    setSelectedUser(null);
    setSelectedBrand(null);
    setSelectedType(null);
    setSelectedModel(null);
    setSelectedOffice(null);
    setOfficeInput('');
    search(initialFilter);
  };

  const handleSearch = () => {
    search(localFilter);
  };

  const handleUpdateFilter = useCallback(
    (updatedFilter: Partial<AssetsFilter>) => {
      setLocalFilter((prev) => ({ ...prev, ...updatedFilter }));
    },
    []
  );

  const { assetStatus } = useAssetOptions();
  const { officeOptions } = useOfficesOptions({ search: officeInput });

  const { userOptions, isUsersLoading } = useUsersOptions({
    search: userInput,
  });

  const handleUserSelect = useCallback(
    (option: Option | null) => {
      setSelectedUser(option);
      handleUpdateFilter({
        userId: option?.data?.id || '',
      });
    },
    [handleUpdateFilter]
  );

  const handleBrandSelect = useCallback(
    (option: Option | null) => {
      setSelectedBrand(option);
      setSelectedModel(null);
      handleUpdateFilter({
        brandId: option?.data || '',
        modelId: '',
      });
    },
    [handleUpdateFilter]
  );

  const handleTypeSelect = useCallback(
    (option: Option | null) => {
      setSelectedType(option);
      handleUpdateFilter({
        typeId: option?.data || '',
      });
    },
    [handleUpdateFilter]
  );

  const handleModelSelect = useCallback(
    (option: Option | null) => {
      setSelectedModel(option);
      handleUpdateFilter({
        modelId: option?.data || '',
      });
    },
    [handleUpdateFilter]
  );

  const handleOfficeSelect = useCallback(
    (option: Option | null) => {
      setSelectedOffice(option);
      handleUpdateFilter({
        officeId: option?.data || '',
      });
    },
    [handleUpdateFilter]
  );

  return (
    <div className='w-full p-5 flex flex-col justify-center bg-white rounded-sm shadow-xl gap-5'>
      <div className='grid grid-cols-4 gap-3'>
        <TextInput
          name='barcode-asset-search'
          placeholder='Buscar por código de barras'
          value={localFilter.barcode || ''}
          onChange={(value) =>
            handleUpdateFilter({
              barcode: value.toString(),
            })
          }
        />
        <TypeAutocompleteInput
          name='type-asset-search'
          placeholder='Buscar tipo'
          value={selectedType}
          onChange={handleTypeSelect}
          onInputChange={() => {}}
          showCreateButton={false}
        />
        <BrandAutocompleteInput
          name='brand-asset-search'
          placeholder='Buscar marca'
          value={selectedBrand}
          onChange={handleBrandSelect}
          onInputChange={() => {}}
          showCreateButton={false}
        />
        <AssetModelAutocompleteInput
          name='model-asset-search'
          placeholder={
            selectedBrand ? 'Buscar modelo' : 'Selecciona una marca primero'
          }
          value={selectedModel}
          onChange={handleModelSelect}
          onInputChange={() => {}}
          showCreateButton={false}
          brandId={selectedBrand?.data}
          disabled={!selectedBrand}
        />
      </div>

      <div className='grid grid-cols-4 gap-3'>
        <AutocompleteInput
          name='office-asset-search'
          placeholder='Buscar oficina o ciudad'
          value={selectedOffice}
          onChange={handleOfficeSelect}
          onInputChange={setOfficeInput}
          options={officeOptions}
          required={false}
        />
        <AutocompleteInput
          name='user-asset-search'
          placeholder='Buscar usuario'
          value={selectedUser}
          onChange={handleUserSelect}
          onInputChange={setUserInput}
          options={userOptions}
          required={false}
          disabled={isUsersLoading}
        />
        <SelectInput
          name='status-asset-search'
          placeholder='Seleccione un estado'
          value={localFilter.statuses?.[0] || ''}
          options={assetStatus}
          onChange={(statusValue) =>
            setLocalFilter({
              ...localFilter,
              statuses: [statusValue.toString()],
            })
          }
        />
      </div>

      <div className='flex gap-3 justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={resetFilter}
        >
          <TrashIcon color='primary' />
          Limpiar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={handleSearch}
        >
          <SearchIcon color='primary' />
          Buscar
        </GlobalButton>
      </div>
    </div>
  );
};

export default SearchBar;
